import React from 'react'
import { useTranslation } from 'react-i18next'

import { AddressForm } from './AddressForm'
import { CodeForm } from './CodeForm'
import { EmailForm } from './EmailForm'
import { InquiryForm } from './InquiryForm'
import { InvoiceAddresseeForm } from './InvoiceAddresseeForm'
import { PaymentForm } from './PaymentForm'
import { PersonalForm } from './PersonalForm'
import { UsageInfoForm } from './UsageInfoForm'

import { Col, Container, Row, Section, Text } from '../../basics'

type RegistrationFormProps = {
  disabledInquiryForm?: boolean
  enabledCodeForm?: boolean
  showCompanyName?: boolean
  showPurposeOfUse?: boolean
  showCheckInTime?: boolean
  requiresPayment?: boolean
  // Card-free plans split further: on-site (現地決済) vs invoice-billed
  // (請求書払い) show different guest-facing notes (SB-2127).
  isInvoicePayment?: boolean
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  disabledInquiryForm = false,
  enabledCodeForm = false,
  showCompanyName = false,
  showPurposeOfUse = false,
  showCheckInTime = false,
  requiresPayment = true,
  isInvoicePayment = false,
}) => {
  const showUsageInfoForm =
    showCompanyName || showPurposeOfUse || showCheckInTime
  const { t } = useTranslation()
  return (
    <React.Fragment>
      <Section>
        <div className="pc">
          <Text size={22} weight="bold">
            {t(`components.RegistrationForm.registrationTitle`)}
          </Text>
        </div>
      </Section>
      <Section title={t(`components.AddressForm.title`)}>
        <Container>
          <Row gutterWidth={35}>
            <Col pc={6}>
              <PersonalForm />
            </Col>
            <Col pc={6}>
              <AddressForm />
            </Col>
          </Row>
        </Container>
      </Section>
      <Section title={t(`components.EmailForm.title`)}>
        <Container>
          <Row gutterWidth={35}>
            <Col>
              <EmailForm />
            </Col>
          </Row>
        </Container>
      </Section>

      {enabledCodeForm && (
        <Section title={t(`components.CodeForm.title`)}>
          <Container>
            <Row gutterWidth={35}>
              <Col>
                <CodeForm />
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {showUsageInfoForm && (
        <Section title={t(`components.UsageInfoForm.title`)}>
          <Container>
            <Row gutterWidth={35}>
              <Col>
                <UsageInfoForm
                  showCompanyName={showCompanyName}
                  showPurposeOfUse={showPurposeOfUse}
                  showCheckInTime={showCheckInTime}
                />
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {!disabledInquiryForm && (
        <Section title={t(`components.InquiryForm.title`)}>
          <Container>
            <Row gutterWidth={35}>
              <Col>
                <InquiryForm />
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {requiresPayment ? (
        <Section title={t(`components.PaymentForm.title`)}>
          <Container>
            <Row gutterWidth={35}>
              <Col>
                <PaymentForm />
              </Col>
            </Row>
          </Container>
        </Section>
      ) : (
        <Section title={t(`components.PaymentForm.title`)}>
          <Container>
            <Row gutterWidth={35}>
              <Col>
                <Text>
                  {t(
                    isInvoicePayment
                      ? `components.RegistrationForm.invoicePaymentNote`
                      : `components.RegistrationForm.onSitePaymentNote`
                  )}
                </Text>
              </Col>
            </Row>
            {isInvoicePayment && (
              <Row gutterWidth={35}>
                <Col>
                  <InvoiceAddresseeForm />
                </Col>
              </Row>
            )}
          </Container>
        </Section>
      )}
    </React.Fragment>
  )
}
